/**
 * ProfileSetupModal Component (Two-Step Flow)
 *
 * Step 1: Profile Setup
 * - Avatar upload with circle preview
 * - First name and last name (required, auto-filled from Stripe if available)
 * - Phone number (optional - can skip verification)
 *
 * Step 2: Phone Verification (skippable)
 * - 6-digit SMS OTP code sent via Twilio
 * - Can go back to change phone number
 * - Can skip verification if SMS fails or user prefers to verify later
 *
 * Cannot close until profile is saved (name required), but phone verification is optional
 */

import { useState, useEffect, useRef } from 'react'
import { Modal } from '../ui/Modal'
import { getStripeCustomerData, linkProfile, requestPhoneOTP, verifyPhoneOTP } from '@/services/customerService'
import { uploadAvatar } from '@/services/uploadService'
import type { User } from '@/features/auth/types'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'

interface ProfileSetupModalProps {
  isOpen: boolean
  user: User
  onComplete: () => void
}

type SetupStep = 'profile' | 'verification'

export function ProfileSetupModal({ isOpen, user, onComplete }: ProfileSetupModalProps) {
  // Step management
  const [currentStep, setCurrentStep] = useState<SetupStep>('profile')

  // Profile form state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneFromApplication] = useState(false) // Phone from application is never pre-verified — OTP always required at onboarding
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)

  // Verification state
  const [verificationCode, setVerificationCode] = useState('')
  const [maskedPhone, setMaskedPhone] = useState('')
  const [canResend, setCanResend] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(30)

  // UI state
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [otpSendFailed, setOtpSendFailed] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<{
    firstName?: string
    lastName?: string
    phone?: string
  }>({})

  // Store user ID from profile linking for verification step
  const [linkedUserId, setLinkedUserId] = useState<string>(user.id)

  const fileInputRef = useRef<HTMLInputElement>(null)

  // Fetch customer data and parse name on mount
  // Priority: 1) Application phone, 2) User profile data, 3) Stripe customer data
  useEffect(() => {
    async function fetchAndParseName() {
      if (!isOpen || !user.email) return

      try {
        setIsLoading(true)

        // FIRST: Check if user has an application with verified phone
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
        const token = localStorage.getItem('auth_token')
        try {
          const appResponse = await fetch(`${API_URL}/api/auth/application-phone?email=${encodeURIComponent(user.email)}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
          if (appResponse.ok) {
            const appData = await appResponse.json()
            if (appData.phone_number) {
              setPhone(appData.phone_number)
            }
          }
        } catch (err) {
          // no-op
        }

        // SECOND: Try to pre-fill from existing user profile data (from application or previous login)
        // Check if user object already has name/phone from the profiles table
        if (user.firstName || (user as any).first_name) {
          const existingFirstName = user.firstName || (user as any).first_name || ''
          const existingLastName = user.lastName || (user as any).last_name || ''
          const existingPhone = (user as any).phone_number || (user as any).phoneNumber || ''

          if (existingFirstName) setFirstName(existingFirstName)
          if (existingLastName) setLastName(existingLastName)
          if (existingPhone) setPhone(existingPhone)

          // If we have all the data, we're done
          if (existingFirstName && existingLastName) {
            setIsLoading(false)
            return
          }
        }

        // SECOND: Try to fetch from backend profile
        try {
          const profileResponse = await fetch(`${API_URL}/api/subscriptions/access/level`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
          if (profileResponse.ok) {
            await profileResponse.json()
            // The access/level endpoint might have user metadata
          }
        } catch (profileErr) {
          // no-op
        }

        // Check if user has pending invite token (invited user, not existing Stripe customer)
        const pendingInvite = localStorage.getItem('pending_invite_token')

        if (pendingInvite && firstName && lastName) {
          setIsLoading(false)
          return
        }

        // THIRD: Try Stripe customer data as fallback
        if (!firstName || !lastName) {
          try {
            const customerData = await getStripeCustomerData(user.email)

            // Parse name from customer_benefits.name field (only if not already set)
            if (customerData.name && !firstName) {
              const parsed = parseFullName(customerData.name)
              if (!firstName) setFirstName(parsed.firstName)
              if (!lastName) setLastName(parsed.lastName)
            }

            // Pre-fill phone if already exists but not verified
            if (customerData.phoneNumber && !phone) {
              setPhone(customerData.phoneNumber)
            }
          } catch (stripeErr) {
            // no-op
          }
        }
      } catch (err) {
        console.error('[ProfileSetupModal] Error fetching data:', err)
        // Don't show error - user can still fill manually
      } finally {
        setIsLoading(false)
      }
    }

    fetchAndParseName()
  }, [isOpen, user.email, user.firstName, user.lastName])

  // Resend countdown timer
  useEffect(() => {
    if (currentStep === 'verification' && !canResend && resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(prev => prev - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (resendCountdown === 0) {
      setCanResend(true)
    }
  }, [currentStep, canResend, resendCountdown])

  /**
   * Parse full name into first and last names
   */
  function parseFullName(name: string | null): { firstName: string; lastName: string } {
    if (!name || !name.trim()) {
      return { firstName: '', lastName: '' }
    }

    const parts = name.trim().split(/\s+/)

    if (parts.length === 0) {
      return { firstName: '', lastName: '' }
    }
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' }
    }

    // First part = first name, rest = last name
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' ')
    }
  }

  /**
   * Handle avatar file selection
   */
  function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg', 'image/gif']
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPG, PNG, WEBP, or GIF image')
      return
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024
    if (file.size > maxSize) {
      setError('Image must be under 5MB')
      return
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file)
    setAvatarFile(file)
    setAvatarPreview(previewUrl)
    setError(null)
  }

  /**
   * Validate phone number format (international support via react-phone-number-input)
   */
  function validatePhoneNumber(phoneNumber: string): boolean {
    if (!phoneNumber) return false
    // Use the library's built-in validation which handles international formats
    return isValidPhoneNumber(phoneNumber)
  }

  /**
   * Validate form fields for Step 1 (Profile)
   */
  function validateProfileForm(): boolean {
    const errors: { firstName?: string; lastName?: string; phone?: string } = {}

    if (!firstName.trim()) {
      errors.firstName = 'First name is required'
    }
    if (!lastName.trim()) {
      errors.lastName = 'Last name is required'
    }
    if (phone.trim() && !validatePhoneNumber(phone)) {
      errors.phone = 'Please enter a valid phone number'
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  /**
   * Handle Step 1 submission (Save profile and send OTP)
   */
  async function handleProfileSubmit() {
    // Validate form
    if (!validateProfileForm()) {
      return
    }

    setIsSubmitting(true)
    setError(null)
    setOtpSendFailed(false)

    try {
      // Step 1: Upload avatar if provided
      let avatarUrl: string | null = null
      if (avatarFile) {
        avatarUrl = await uploadAvatar(avatarFile, user.id)
      }

      // Step 2: Link profile with all data
      // If phone is from application, it's pre-verified - pass flag to backend
      const response = await linkProfile({
        userId: user.id,
        email: user.email,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        avatarUrl: avatarUrl,
        phonePreVerified: phoneFromApplication // Skip phone verification if from application
      })

      setLinkedUserId(response.profile.id)

      // Check if user has an auto-generated invite (customer_benefits users)
      if (response.hasInvite && response.inviteToken) {
        localStorage.setItem('pending_invite_token', response.inviteToken)
        localStorage.setItem('has_pending_subscription', 'true') // Flag to show subscription modal
      }

      // If no phone entered, skip verification entirely
      if (!phone.trim()) {
        onComplete()
        return
      }

      // If phone was pre-verified from application, skip OTP step entirely
      if (phoneFromApplication) {
        localStorage.removeItem('application_phone') // Clean up
        onComplete() // Done - no OTP needed
        return
      }

      // === TWILIO DISABLED — skip phone OTP verification (revert when Twilio is approved) ===
      // Phone number is still saved to the profile above, just no OTP verification
      onComplete()
      return
      // === END TWILIO DISABLED ===
      /* Original OTP code (uncomment to re-enable):
      try {
        const otpResponse = await requestPhoneOTP(phone.trim())
        setMaskedPhone(otpResponse.maskedPhone || phone)
        setCurrentStep('verification')
        setCanResend(false)
        setResendCountdown(30)
      } catch (otpErr) {
        console.error('[ProfileSetupModal] OTP send failed:', otpErr)
        onComplete()
        return
      }
      */
    } catch (err) {
      // Profile save failed — cannot skip this
      console.error('[ProfileSetupModal] Error in profile submit:', err)
      setError(err instanceof Error ? err.message : 'Failed to save profile. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Handle phone verification submission
   */
  async function handleVerificationSubmit() {
    if (verificationCode.length !== 6) {
      setError('Please enter the 6-digit verification code')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      await verifyPhoneOTP(phone.trim(), verificationCode, linkedUserId)

      // Complete setup - user can now access the app
      onComplete()
    } catch (err) {
      console.error('[ProfileSetupModal] Verification error:', err)
      setError(err instanceof Error ? err.message : 'Invalid verification code. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Go back to profile step to change phone number
   */
  function handleBackToProfile() {
    setCurrentStep('profile')
    setVerificationCode('')
    setError(null)
  }

  /**
   * Resend verification code
   */
  async function handleResendCode() {
    if (!canResend) return

    setIsSubmitting(true)
    setError(null)

    try {
      const otpResponse = await requestPhoneOTP(phone.trim())
      setMaskedPhone(otpResponse.maskedPhone || phone)

      // Reset countdown
      setCanResend(false)
      setResendCountdown(30)
      setVerificationCode('')
    } catch (err) {
      console.error('[ProfileSetupModal] Resend error:', err)
      setError(err instanceof Error ? err.message : 'Failed to resend code. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Check if profile form is valid (for button state)
  const isProfileFormValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    (!phone.trim() || validatePhoneNumber(phone))

  // Prevent modal from closing (pass no-op function)
  const preventClose = () => {
    // Do nothing - modal cannot be closed until complete
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={preventClose}
      showCloseButton={false}
      maxWidth="max-w-lg"
    >
      <div className="space-y-6">
        {/* === STEP 1: PROFILE SETUP === */}
        {currentStep === 'profile' && (
          <>
            {/* Header */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">
                Complete Your Profile
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Set up your profile to get started
              </p>
            </div>

            {/* Loading State */}
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading your information...</p>
              </div>
            ) : (
              <>
                {/* Avatar Upload Section */}
                <div className="flex flex-col items-center space-y-4">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                      {avatarPreview ? (
                        <img
                          src={avatarPreview}
                          alt="Avatar preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <svg
                          className="w-12 h-12 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      )}
                    </div>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/jpg,image/gif"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    {avatarPreview ? 'Change Photo' : 'Upload Photo (Optional)'}
                  </button>
                </div>

                {/* Form Fields */}
                <div className="space-y-4">
                  {/* Email (Read-only) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={user.email}
                      readOnly
                      className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg cursor-not-allowed text-gray-600"
                    />
                  </div>

                  {/* First Name (Required) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value)
                        setFieldErrors(prev => ({ ...prev, firstName: undefined }))
                      }}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        fieldErrors.firstName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your first name"
                    />
                    {fieldErrors.firstName && (
                      <p className="mt-1 text-sm text-red-500">{fieldErrors.firstName}</p>
                    )}
                  </div>

                  {/* Last Name (Required) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value)
                        setFieldErrors(prev => ({ ...prev, lastName: undefined }))
                      }}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        fieldErrors.lastName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your last name"
                    />
                    {fieldErrors.lastName && (
                      <p className="mt-1 text-sm text-red-500">{fieldErrors.lastName}</p>
                    )}
                  </div>

                  {/* Phone Number (Optional) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                      {phoneFromApplication && (
                        <span className="ml-2 text-xs text-green-600 font-normal">(Verified from application)</span>
                      )}
                    </label>
                    <PhoneInput
                      international
                      defaultCountry="US"
                      value={phone}
                      onChange={(value) => {
                        if (!phoneFromApplication) {
                          setPhone(value || '')
                          setFieldErrors(prev => ({ ...prev, phone: undefined }))
                        }
                      }}
                      disabled={phoneFromApplication}
                      className={`phone-input-custom ${fieldErrors.phone ? 'phone-input-error' : ''} ${phoneFromApplication ? 'phone-input-disabled' : ''}`}
                      placeholder="Enter phone number"
                    />
                    {fieldErrors.phone && (
                      <p className="mt-1 text-sm text-red-500">{fieldErrors.phone}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      {phoneFromApplication
                        ? 'Already verified during application'
                        : "We will notify you of any missed messages within the app to the phone number. You can skip this step."
                      }
                    </p>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                {/* Skip Verification (shown when OTP send failed but profile was saved) */}
                {otpSendFailed && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600 text-center">
                      Your profile was saved. You can verify your phone later from settings.
                    </p>
                    <button
                      onClick={onComplete}
                      className="w-full py-3 px-4 rounded-lg font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition-all"
                    >
                      Skip Verification for Now
                    </button>
                  </div>
                )}

                {/* Continue Button */}
                <button
                  onClick={handleProfileSubmit}
                  disabled={!isProfileFormValid || isSubmitting}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                    isProfileFormValid && !isSubmitting
                      ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    phone.trim() ? 'Continue to Verification' : 'Continue'
                  )}
                </button>

                <p className="text-xs text-center text-gray-500">
                  <span className="text-red-500">*</span> Required fields
                </p>
              </>
            )}
          </>
        )}

        {/* === STEP 2: PHONE VERIFICATION === */}
        {currentStep === 'verification' && (
          <>
            {/* Header */}
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Verify Your Phone
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                We sent a verification code to
              </p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {maskedPhone}
              </p>
            </div>

            {/* Verification Code Input */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                  Enter 6-Digit Code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '')
                    setVerificationCode(value)
                    setError(null)
                  }}
                  className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="000000"
                  autoComplete="one-time-code"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Verify Button */}
              <button
                onClick={handleVerificationSubmit}
                disabled={verificationCode.length !== 6 || isSubmitting}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                  verificationCode.length === 6 && !isSubmitting
                    ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                    Verifying...
                  </span>
                ) : (
                  'Verify Phone Number'
                )}
              </button>

              {/* Resend Code */}
              <div className="text-center">
                {canResend ? (
                  <button
                    onClick={handleResendCode}
                    disabled={isSubmitting}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400"
                  >
                    Resend Code
                  </button>
                ) : (
                  <p className="text-sm text-gray-500">
                    Resend code in {resendCountdown}s
                  </p>
                )}
              </div>

              {/* Change Phone Number */}
              <div className="text-center pt-4 border-t border-gray-200">
                <button
                  onClick={handleBackToProfile}
                  disabled={isSubmitting}
                  className="text-sm text-gray-600 hover:text-gray-800 font-medium disabled:text-gray-400"
                >
                  ← Change Phone Number
                </button>
              </div>

              {/* Skip verification option */}
              <div className="text-center pt-2">
                <button
                  onClick={onComplete}
                  disabled={isSubmitting}
                  className="text-sm text-gray-500 hover:text-gray-700 font-medium disabled:text-gray-400"
                >
                  Skip verification for now
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
